import React, { useState, useRef, useEffect } from "react";
import "./UpdatePackage.css";
import { TextField, Autocomplete } from "@mui/material";
import Chip from "@mui/material/Chip";
import {
  TravelerSelector,
  SingleCalendar,
} from "../../../../components";
import { axiosGet } from "../../../../utils/apis/api";
import GlobalModal from "../AddModal/GlobalModal";
import { useTranslation } from "../../../../contexts/TranslationContext";
import dayjs from "dayjs";
import Cookies from "js-cookie";

const UpdatePackage = ({ url, data, onReturn, onUpdate }) => {
  const [mainImage, setMainImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([null]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [showPopup, setShowPopUp] = useState(false);
  const fileInputRef = useRef(null);
  const [packageData, setPackageData] = useState({
    packageName: "",
    countries: [],
    travelers: {
      adults: 1,
      children: 0,
      children_ages: [],
      infant_num: 0
    },
    startDate: "",
    endDate: "",
  });
  const [locations, setLocations] = useState(null);
  const [hasMorelocations, setHasMorelocations] = useState(true);
  const PAGE_LIMIT = 50;
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const Lang = Cookies.get("preferred_language") || "en";

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImage({ preview: reader.result, file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedImages = [...additionalImages];
        updatedImages[index] = { preview: reader.result, file };

        const newList =
          index === additionalImages.length - 1
            ? [...updatedImages, null]
            : updatedImages;

        setAdditionalImages(newList);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPackageData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountrySelect = (event, newValue) => {
    setPackageData((prev) => ({
      ...prev,
      countries: newValue,
    }));
  };

  const handleDelete = (countryToDelete) => {
    setPackageData((prev) => ({
      ...prev,
      countries: prev.countries.filter((country) => {
        const country_id = country.row_id ?? null;
        const country_delete_id = countryToDelete.row_id ?? null;
        const country_name = country.name ?? null;
        const country_delete_name = countryToDelete.name ?? null;
        // Remove the country ONLY IF both id and name match
        const isSameCountry =
          country_id === country_delete_id &&
          country_name === country_delete_name;

        return !isSameCountry; // keep all except the matched one
      }),
    }));
  };

  const fetchLocations = async (search, newStart = 0, append = false) => {
    if (!hasMorelocations && append) return;
    try {
      var parameters = {
        start: newStart,
        limit: PAGE_LIMIT,
      };
      if (search) {
        parameters["search"] = search;
      }

      const response = await axiosGet(
        `${url}/api/method/alltimes.www.api.locations`,
        parameters
      );
      const newItems = Array.isArray(response.result?.locations)
        ? response.result?.locations
        : []; // Ensure array
      setLocations((prevItems) =>
        append ? [...prevItems, ...newItems] : newItems
      );
      setHasMorelocations(newItems.length === PAGE_LIMIT);
    } catch (error) {
      setLocations([]);
    } finally {
    }
  };

  useEffect(() => {
    fetchLocations();
    if (data?.details) {
      let destination_list = [];
      data?.details?.destinations.forEach((dest) => {
        destination_list.push({
          row_id: dest.primary_destination_row_id, // for old destination to compare in backend
          name: dest.location,
          location_name: dest.location_name,
          location_type: dest.location_type,
          country: dest.country_location,
          country_code: dest.country_code,
          location_docname: dest.location_docname,
        });
      });
      if (destination_list.length > 0) {
        setSelectedCountries(destination_list);
      }

      setPackageData({
        packageName: data?.details?.package_name,
        countries: destination_list,
        travelers: {
          adults: data?.details?.number_of_adults ?? 1,
          children: data?.details?.number_of_children ?? 0,
          children_ages: data?.details?.ages ?? [],
          infant_num: data?.details?.infant_num ?? 0,
        },
        startDate: data?.details?.start_date ?? "",
        endDate: data?.details?.end_date ?? "",
      });
    }
  }, [data]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!packageData.packageName.trim()) {
      newErrors.packageName = t("PackageNameRequired");
    }
    if (packageData.countries.length === 0) {
      newErrors.countries = t("PackageCountriesRequired");
    }
    if (!packageData.startDate || !packageData.endDate) {
      newErrors.date = t("PackageDateRequired");
    }
    if (
      packageData.travelers.children > 0 &&
      (packageData.travelers.children_ages.length <
        packageData.travelers.children ||
        packageData.travelers.children_ages.includes(null) ||
        packageData.travelers.children_ages.includes(0))
    ) {
      newErrors.childrenAges = t("PackageChildrenAgesRequired");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // No errors: proceed with submission
    setErrors({});
    setShowPopUp(true);
  };

  return (
    <div className="hotel-form-container">
      <button className="hotel-form-backbtn" onClick={onReturn}>
      {Lang === "ar" ? (
          <>
            {t("BackToPlan")}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 23 23"
              fill="none"
            >
              <path
                d="M5.75 11.5H18.2083M5.75 11.5L9.58333 7.66671M5.75 11.5L9.58333 15.3334"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 23 23"
              fill="none"
            >
              <path
                d="M5.75 11.5H18.2083M5.75 11.5L9.58333 7.66671M5.75 11.5L9.58333 15.3334"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("BackToPlan")}
          </>
        )}
      </button>
      {/* Image Upload Section */}
      {/* <div className='package-image-upload-container cover'>
        <label htmlFor='upload-cover' className='package-image-upload-box cover'>
          {mainImage ? (
            <>
              <button
                type="button"
                className="remove-image-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setMainImage(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                &times;
              </button>
              <img src={mainImage.preview} alt="Main Cover" className='package-image-preview cover' />
            </>
          ) : (
            <>
              <div className='package-image-text'>Add Package <span>Cover</span></div>
              <div className='package-image-icon'>
                <svg xmlns="http://www.w3.org/2000/svg" width="41" height="32" viewBox="0 0 39 31" fill="none">
                  <path d="M1 19.5V1H38V29.7778H1V19.5Z" stroke="#C1C1C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 23.6107L9.22222 17.444L15.3889 21.5551L27.7222 11.2773L38 19.4996" stroke="#C1C1C1" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12.8334 10.2503C12.8334 11.6771 11.6768 12.8337 10.25 12.8337C8.82328 12.8337 7.66669 11.6771 7.66669 10.2503C7.66669 8.82359 8.82328 7.66699 10.25 7.66699C11.6768 7.66699 12.8334 8.82359 12.8334 10.2503Z" fill="#D7D3D3" stroke="#C1C1C1"/>
                  <path d="M15.1115 21.9721L15.4222 22.1792L15.709 21.9402L27.7287 11.9238L37.5 19.7409V29.2783H1.5V23.8617L9.23922 18.0572L15.1115 21.9721Z" fill="#D7D3D3" stroke="#C1C1C1"/>
                </svg>
              </div>
              <div className='package-image-text-secondary'>
                Drag & drop image or <span className='browse-text'>Browse</span>
              </div>
              <div className='package-image-format-note'>Supported formats: JPEG, PNG</div>
            </>
          )}
        </label>
        <input
          id='upload-cover'
          type='file'
          accept='image/jpeg, image/png'
          onChange={handleMainImageChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
      </div> */}

      {/* Additional Images */}
      {/* {mainImage && (
        <div className='package-images-wrapper'>
          {additionalImages.map((img, idx) => (
            <div key={idx} className='package-image-upload-container small'>
              <label htmlFor={`upload-additional-${idx}`} className='package-image-upload-box'>
                {img ? (
                  <>
                    <img src={img?.preview} alt={`Preview ${idx}`} className='package-image-preview' />
                    <button
                      className='remove-image-btn'
                      onClick={(e) => {
                        e.preventDefault();
                        const updatedImages = [...additionalImages];
                        updatedImages[idx] = null;
                        // Shift remaining images
                        const filteredImages = updatedImages.filter((_, i) => i !== idx);
                        // Add null at end if needed
                        setAdditionalImages(filteredImages.some(i => i === null) 
                          ? filteredImages 
                          : [...filteredImages, null]
                        );
                      }}
                    >
                      &times;
                    </button>
                  </>
                ) : (
                  <div className='package-add-more-box'>
                    Add More<br /><strong>Image</strong>
                  </div>
                )}
              </label>
              <input
                id={`upload-additional-${idx}`}
                type='file'
                accept='image/jpeg, image/png'
                onChange={(e) => handleAdditionalImageChange(e, idx)}
                style={{ display: 'none' }}
              />
            </div>
          ))}
        </div>
      )} */}

      {/* Package Details Form */}
      <div className="update-package-form-title-container">
        <div className="update-package-form-title">{t("PackageDetails")}</div>
        <div className="update-package-form-desc">{t("UpdateDescription")}</div>
      </div>

      <div className="row update-package-row">
        <div className="col-md-6">
          <div className="update-package-form-input-container">
            <label className="update-package-form-input-label">
              {t("PackageName")}
            </label>
            <input
              name="packageName"
              className="update-package-form-input"
              type="text"
              placeholder="Enter package name"
              value={packageData.packageName}
              onChange={handleInputChange}
            />
            {errors.packageName && packageData.packageName === "" && (
              <div className="invalid-feedback">{errors.packageName}</div>
            )}
          </div>
        </div>

        <div className="col-md-6">
          <div className="update-package-form-input-container">
            <label className="update-package-form-input-label">
              {t("WhichCountries")}
            </label>
            <Autocomplete
              style={{
                marginTop: "unset"
              }}
              dir={Lang === "ar" ? "rtl" : "ltr"}
              multiple
              options={locations}
              getOptionLabel={(option) => option.location_name}
              value={packageData.countries}
              onChange={handleCountrySelect}
              renderTags={() => null}
              isOptionEqualToValue={(option, value) =>
                option.location_name === value.location_name
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("SelectCountries")}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      direction: Lang === "ar" ? "rtl" : "ltr",
                    },
                    sx: {
                      backgroundColor: "#FFF",
                      borderRadius: "6px",
                      border: `1px solid #E9ECEF`,
                      padding: "9px 15px !important",
                      fontFamily: '"Dmsans400"',
                      fontSize: "14px !important",
                      fontWeight: 400,
                      letterSpacing: "0.25px",
                    },
                  }}
                />
              )}
              sx={{
                width: "100%",
                mt: 1,

                ".MuiAutocomplete-inputRoot": {
                  fontSize: "12px",
                  flexDirection: Lang === "ar" ? "row-reverse" : "row",
                },
                ".MuiAutocomplete-endAdornment": {
                  left: Lang === "ar" ? "9 !important" : "unset !important",
                  right: Lang === "ar" ? "unset !important" : "9 !important",
                },
                ".MuiInputBase-input": {
                  textAlign: Lang === "ar" ? "right" : "left",
                },
              }}
            />

            {errors.countries && packageData.countries.length === 0 && (
              <div className="invalid-feedback">{errors.countries}</div>
            )}
            <div
              className="modal-selected-countries update-package-countries-selected"
              style={{ marginTop: "15px" }}
            >
              {packageData.countries &&
                packageData.countries.map((country) => (
                  <Chip
                    key={country.row_id ?? country.name}
                    label={country.location_name}
                    onDelete={() => handleDelete(country)}
                    variant="outlined"
                    className="countries-chip"
                    sx={{
                      margin: "0 5px 5px 0",
                      background: "rgba(37, 74, 165, 0.07)",
                      color: "#27346A",
                      fontSize: "14px",
                      fontFamily: "Dmsans400",
                      border: "unset",
                      direction: Lang === "ar" ? "rtl" : "ltr",

                      "& .MuiChip-deleteIcon": {
                        fill: "#27346A",
                        opacity: "0.26",
                        marginLeft: Lang === "ar" ? "6px" : "0",
                        marginRight: Lang !== "ar" ? "6px" : "0",
                      },
                    }}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
      <div className="row update-package-data-container">
        <div className="col-md-6">
          <div className="update-package-form-input-container">
            <label className="update-package-form-input-label">
              {t("NumberOfTravelers")}
            </label>
            <TravelerSelector
              adults={packageData.travelers.adults}
              children={packageData.travelers.children}
              childrenAges={packageData.travelers.children_ages}
              infants={packageData.travelers.infant_num}
              onAdultsChange={(newAdults) =>
                setPackageData((prev) => ({
                  ...prev,
                  travelers: {
                    ...prev.travelers,
                    adults: newAdults,
                  },
                }))
              }
              onInfantsChange={(newInfants) =>
                setPackageData((prev) => ({
                  ...prev,
                  travelers: {
                    ...prev.travelers,
                    infant_num: newInfants,
                  },
                }))
              }
              onChildrenChange={(newChildren) =>
                setPackageData((prev) => ({
                  ...prev,
                  travelers: {
                    ...prev.travelers,
                    children: newChildren,
                  },
                }))
              }
              onChildrenAgesChange={(newAges) =>
                setPackageData((prev) => ({
                  ...prev,
                  travelers: {
                    ...prev.travelers,
                    children_ages: newAges,
                  },
                }))
              }
            />
            {errors.childrenAges &&
              (packageData.travelers.children_ages.includes(null) ||
                packageData.travelers.children_ages.includes(0)) && (
                <div className="invalid-feedback">{errors.childrenAges}</div>
              )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="update-package-form-input-container">
            <div className="update-package-date-labels">
              <div className="update-package-date-div">
                <label className="update-package-form-input-label date">
                  {t("StartDate")}
                </label>
                <SingleCalendar
                  value={
                    packageData.startDate
                      ? new Date(packageData.startDate)
                      : null
                  }
                  onChange={(e) => {
                    const selectedDate = e?.value;

                    if (selectedDate) {
                      setPackageData((prev) => ({
                        ...prev,
                        startDate: dayjs(selectedDate).format(
                          "YYYY-MM-DD HH:mm:ss"
                        ),
                      }));
                    }
                  }}
                  min={new Date()}
                  max={
                    packageData.endDate
                      ? new Date(packageData.endDate)
                      : null
                  }
                  showTime= {false}
                />
              </div>
              <div className="update-package-date-div">
                <label className="update-package-form-input-label date">
                  {t("EndDate")}
                </label>
                <SingleCalendar
                  value={
                    packageData.endDate ? new Date(packageData.endDate) : null
                  }
                  onChange={() => {}}
                  disabled={true}
                  placeholder={
                    packageData.endDate
                      ? dayjs(packageData.endDate).format("YYYY-MM-DD HH:mm:ss")
                      : "End Date"
                  }
                  showTime= {false}
                />
              </div>

              {errors.date &&
                (!packageData.startDate || !packageData.endDate) && (
                  <div className="invalid-feedback">{errors.date}</div>
                )}
            </div>
          </div>
        </div>
      </div>

      <div className="update-package-btn-container">
        <div
          className="hotel-form-addbtn"
          onClick={(e) => {
            handleSubmit(e);
          }}
        >
          {t("Save")}
        </div>
      </div>
      <GlobalModal
        title={t("UpdatePackage")}
        subtitle={t("ConfirmationUpdate")}
        globalModal={showPopup}
        onClick={() => {
          var quot_destination = packageData.countries.map((country) => ({
            location: country.name,
            row_id: country.row_id,
          }));
          onUpdate({
            destination_list: quot_destination,
            package_name: packageData.packageName,
            travelers: packageData.travelers,
            start_date: packageData.startDate,
            end_date: packageData.endDate,
          });
        }}
        onClose={() => setShowPopUp(false)}
      ></GlobalModal>
    </div>
  );
};

export default UpdatePackage;
